import React, { useContext, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Box } from "@mui/material";
import {
  LiveKitRoom,
  VideoConference,
  RoomAudioRenderer,
  ConnectionStateToast,
  useConnectionState,
} from "@livekit/components-react";
import "@livekit/components-styles";
import { VideoCallContext } from "components/context/VideoCallContext";
import VideoControlBar from "./VideoControlBar";
import VideoCommentsPanel from "./VideoCommentsPanel";

const VideoLayout: React.FC = () => {
  return (
    <Box sx={{ height: "100%", "& .lk-control-bar": { display: "none" } }}>
      <VideoConference style={{ height: "100%" }} />
    </Box>
  );
};

const ConnectionStateTracker: React.FC = () => {
  const connectionState = useConnectionState();
  const { updateConnectionState } = useContext(VideoCallContext);

  useEffect(() => {
    const isConnected = connectionState === "connected";
    const isConnecting = connectionState === "connecting";
    updateConnectionState(isConnected, isConnecting);
  }, [connectionState]);

  return null;
};

interface PersistentVideoRoomProps {
  containerized?: boolean;
}

const PersistentVideoRoom: React.FC<PersistentVideoRoomProps> = ({
  containerized = false,
}) => {
  const { videoCallState, updateConnectionState, setRenderLocation } =
    useContext(VideoCallContext);
  const roomRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const [commentsOpen, setCommentsOpen] = React.useState(false);

  const { token, shouldRenderLiveKit, renderLocation, userChoices } =
    videoCallState;

  const serverUrl = process.env.REACT_APP_LIVEKIT_SERVER_URL;

  useEffect(() => {
    if (!roomRef.current || containerized) return;

    const positionRoom = (retryCount = 0) => {
      const roomElement = roomRef.current;
      if (!roomElement) return;

      const mainContainer = document.getElementById("video-main-container");
      const pipContainer = document.getElementById("video-pip-container");

      if (renderLocation === "main" && mainContainer) {
        // Position the room over the main container
        const rect = mainContainer.getBoundingClientRect();

        if (rect.width === 0 || rect.height === 0) {
          if (retryCount < 5) {
            setTimeout(() => positionRoom(retryCount + 1), 50);
            return;
          }
        }

        roomElement.style.position = "fixed";
        roomElement.style.top = `${rect.top}px`;
        roomElement.style.left = `${rect.left}px`;
        roomElement.style.width = `${rect.width}px`;
        roomElement.style.height = `${rect.height}px`;
        roomElement.style.zIndex = "1000";
        roomElement.style.visibility = "visible";
        roomElement.style.display = shouldRenderLiveKit ? "block" : "none";
        roomElement.style.pointerEvents = "auto";
        roomElement.style.borderRadius = "12px";
        roomElement.style.overflow = "hidden";
      } else if (renderLocation === "pip" && pipContainer) {
        // Position the room over the PIP container
        const rect = pipContainer.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) {
          if (retryCount < 5) {
            setTimeout(() => positionRoom(retryCount + 1), 50);
            return;
          }
        }

        roomElement.style.position = "fixed";
        roomElement.style.top = `${rect.top - 1}px`;
        roomElement.style.left = `${rect.left}px`;
        roomElement.style.width = `${rect.width}px`;
        roomElement.style.height = `${rect.height + 1}px`;
        roomElement.style.zIndex = "10000";
        roomElement.style.visibility = "visible";
        roomElement.style.display = shouldRenderLiveKit ? "block" : "none";
        roomElement.style.pointerEvents = "auto";
        roomElement.style.borderRadius = "0 0 12px 12px";
        roomElement.style.overflow = "hidden";
      } else {
        roomElement.style.display = "none";
        roomElement.style.visibility = "hidden";
      }
    };

    if (roomRef.current) {
      roomRef.current.style.visibility = "hidden";
    }

    const positionWithDelay = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          positionRoom();
        });
      });
    };

    positionWithDelay();

    const handleUpdate = () => {
      if (shouldRenderLiveKit) {
        positionWithDelay();
      }
    };

    window.addEventListener("resize", handleUpdate);
    window.addEventListener("scroll", handleUpdate);

    // Set up ResizeObserver to watch for container size changes
    let resizeObserver: ResizeObserver | null = null;
    if (renderLocation === "main") {
      const mainContainer = document.getElementById("video-main-container");
      if (mainContainer) {
        resizeObserver = new ResizeObserver(() => {
          handleUpdate();
        });
        resizeObserver.observe(mainContainer);
      }
    } else if (renderLocation === "pip") {
      const pipContainer = document.getElementById("video-pip-container");
      if (pipContainer) {
        resizeObserver = new ResizeObserver(() => {
          handleUpdate();
        });
        resizeObserver.observe(pipContainer);
      }
    }

    // Set up continuous positioning for pip mode to track position changes during drag
    let animationFrame: number | null = null;
    if (renderLocation === "pip") {
      const continuousPosition = () => {
        positionRoom();
        animationFrame = requestAnimationFrame(continuousPosition);
      };
      animationFrame = requestAnimationFrame(continuousPosition);
    }

    return () => {
      window.removeEventListener("resize", handleUpdate);
      window.removeEventListener("scroll", handleUpdate);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [renderLocation, shouldRenderLiveKit, containerized]);

  useEffect(() => {
    if (!shouldRenderLiveKit || !videoCallState.activeVideoRoom) {
      return;
    }

    const checkContainerAvailability = () => {
      const mainContainer = document.getElementById("video-main-container");
      const pipContainer = document.getElementById("video-pip-container");
      const isVideoCallPage = location.pathname.includes("/video/");

      if (renderLocation === "main" && !mainContainer && pipContainer) {
        setRenderLocation("pip");
      }

      if (renderLocation === "pip" && mainContainer && isVideoCallPage) {
        setRenderLocation("main");
      }
    };

    const timeoutId = setTimeout(checkContainerAvailability, 100);
    return () => clearTimeout(timeoutId);
  }, [
    location.pathname,
    renderLocation,
    setRenderLocation,
    shouldRenderLiveKit,
    videoCallState.activeVideoRoom,
  ]);

  useEffect(() => {
    if (renderLocation === "pip" && commentsOpen) {
      setCommentsOpen(false);
    }
  }, [commentsOpen, renderLocation]);

  if (!shouldRenderLiveKit) {
    return null;
  }

  if (!token) {
    return null;
  }

  const LKRoom = LiveKitRoom as any;

  return (
    <Box
      ref={roomRef}
      sx={{
        ...(containerized
          ? {
              width: "100%",
              height: "100%",
              borderRadius: "12px",
              overflow: "hidden",
              minHeight: "500px",
            }
          : {
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1300,
            }),
        visibility: containerized ? "visible" : "hidden",
        display: containerized ? "block" : "none",
      }}
    >
      <LKRoom
        key="truly-persistent-room"
        connect={true}
        token={token}
        serverUrl={serverUrl}
        options={{
          ...(userChoices?.videoDeviceId && {
            videoCaptureDefaults: {
              deviceId: userChoices.videoDeviceId,
            },
          }),
          ...(userChoices?.audioDeviceId && {
            audioCaptureDefaults: {
              deviceId: userChoices.audioDeviceId,
            },
          }),
          autoConnect: true,
          autoPublish: true,
          autoSubscribe: true,
        }}
        video={userChoices?.videoEnabled ?? true}
        audio={userChoices?.audioEnabled ?? true}
        style={{
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          pointerEvents: "auto",
        }}
        onConnected={() => {
          updateConnectionState(true, false);
        }}
        onDisconnected={() => {
          updateConnectionState(false, false);
        }}
        data-lk-theme="default"
      >
        <ConnectionStateTracker />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            flex: 1,
            position: "relative",
            minHeight: 0,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <VideoLayout />
          </Box>
          <VideoCommentsPanel
            open={commentsOpen && renderLocation !== "pip"}
            onClose={() => setCommentsOpen(false)}
            videoRoomUuid={videoCallState.activeVideoRoom?.uuid || ""}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            padding: renderLocation === "pip" ? "8px" : "16px",
            backgroundColor: "transparent",
            pointerEvents: "auto",
            position: "relative",
            zIndex: 10,
          }}
        >
          <VideoControlBar
            commentsOpen={commentsOpen}
            onToggleComments={() => setCommentsOpen((current) => !current)}
          />
        </Box>
        <RoomAudioRenderer />
        <ConnectionStateToast />
      </LKRoom>
    </Box>
  );
};

export default PersistentVideoRoom;
